import Vue from 'vue'
import api from '@/apis/sitePartition'
import {
  RESET_STATE,
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  GET_SITE_PARTITIONS_REQUEST,
  GET_SITE_PARTITIONS_SUCCESS,
  GET_SITE_PARTITIONS_FAILURE,
  GET_PARTITION_ARMING_STATUS_REQUEST,
  GET_PARTITION_ARMING_STATUS_SUCCESS,
  GET_PARTITION_ARMING_STATUS_FAILURE,
  GET_ALL_SITES_PARTITION_ARMING_STATUS_REQUEST,
  GET_ALL_SITES_PARTITION_ARMING_STATUS_SUCCESS,
  GET_ALL_SITES_PARTITION_ARMING_STATUS_FAILURE,
  UPDATE_PARTITION_ARMING_STATUS_REQUEST,
  UPDATE_PARTITION_ARMING_STATUS_SUCCESS,
  UPDATE_PARTITION_ARMING_STATUS_FAILURE,
} from '@/store/mutation-types'

let initialState = {
  partitionsBySite: {},
  armingStatusBySite: {},
  allSitesPartitionArmingStatus: {},
  status: {
    getSitePartitions: null,
    getPartitionArmingStatus: null,
    getAllSitesPartitionArmingStatus: null,
    updatePartitionArmingStatus: null,
  },
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  sitePartitions: function (state) {
    return function (siteId) {
      return state.partitionsBySite[siteId] || []
    }
  },
  partitionArmingStatus: function (state) {
    return function (siteId) {
      return state.armingStatusBySite[siteId] || []
    }
  },
  statusGetSitePartitions: function (state) {
    return state.status.getSitePartitions
  },
  statusGetPartitionArmingStatus: function (state) {
    return state.status.getPartitionArmingStatus
  },
  allSitesPartitionArmingStatus: function (state) {
    return function (siteId) {
      return state.allSitesPartitionArmingStatus[siteId] || []
    }
  },
  allSitesPartitionArmingStatusMap: function (state) {
    return state.allSitesPartitionArmingStatus
  },
  statusUpdatePartitionArmingStatus: function (state) {
    return state.status.updatePartitionArmingStatus
  },
}

// actions
const actions = {
  getSitePartitions: function ({commit, state}, {siteId}) {
    commit(GET_SITE_PARTITIONS_REQUEST, {siteId})
    return new Promise((resolve, reject) => {
      api.getSitePartitions({siteId}).then(
        res => {
          let data = res.body
          commit(GET_SITE_PARTITIONS_SUCCESS, {data, siteId})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_PARTITIONS_FAILURE, {siteId})
          // 404 means partition API not available yet - handle gracefully
          if (err.status === 404) {
            console.log('Partition API not available yet (404)')
            resolve({ body: [] })
          } else {
            reject({
              status: err.status,
              statusText: err.body,
            })
            commit(PROCESS_API_FAILURE, {
              status: err.status,
              statusText: err.body,
              origin: window.location.origin,
              err: Vue.tool.parseToStringify(err),
            })
          }
        }
      )
    })
  },
  getPartitionArmingStatus: function ({commit}, {siteId, sync}) {
    commit(GET_PARTITION_ARMING_STATUS_REQUEST)
    return new Promise((resolve, reject) => {
      api.getArmingStatus({siteId, sync}).then(
        res => {
          let data = res.body
          commit(GET_PARTITION_ARMING_STATUS_SUCCESS, {data, siteId})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_PARTITION_ARMING_STATUS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getAllSitesPartitionArmingStatus: function ({commit}) {
    commit(GET_ALL_SITES_PARTITION_ARMING_STATUS_REQUEST)
    return new Promise((resolve, reject) => {
      api.getAllSitesArmingStatus().then(
        res => {
          const data = res.body
          commit(GET_ALL_SITES_PARTITION_ARMING_STATUS_SUCCESS, {data})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_ALL_SITES_PARTITION_ARMING_STATUS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  updatePartitionArmingStatus: function ({commit, dispatch}, {siteId, partitionIds, armingAction, skipRefresh}) {
    commit(UPDATE_PARTITION_ARMING_STATUS_REQUEST)
    return new Promise((resolve, reject) => {
      api.updatePartitionArmingStatus({siteId, partitionIds, armingAction}).then(
        res => {
          commit(UPDATE_PARTITION_ARMING_STATUS_SUCCESS)
          // PRD §7.4/§13 pre-arm integration + disarm safety net:
          //  arm (1)    → convert queued pre-arm selections into active until-next-disarm bypasses
          //  disarm (2) → clear pre-arm selections; plus re-sync the bypass list, since
          //               until_next_disarm bypasses end server-side with NO client-side expiry and
          //               a camera_bypass.* MQTT event may not arrive. (backend guidance 2026-08-21)
          if (armingAction === 1) {
            dispatch('convertPreArmForPartitions', { siteId, partitionIds })
          } else if (armingAction === 2) {
            dispatch('clearPreArmForPartitions', { siteId, partitionIds })
            dispatch('fetchBypasses', { siteId })
          }
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(UPDATE_PARTITION_ARMING_STATUS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
}

// mutations
const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [GET_SITE_PARTITIONS_REQUEST]: function (state, {siteId}) {
    state.status.getSitePartitions = "requested"
  },
  [GET_SITE_PARTITIONS_SUCCESS]: function (state, {data, siteId}) {
    Vue.set(state.partitionsBySite, siteId, data)
    state.status.getSitePartitions = "successful"
  },
  [GET_SITE_PARTITIONS_FAILURE]: function (state, {siteId}) {
    state.status.getSitePartitions = "failed"
  },
  [GET_PARTITION_ARMING_STATUS_REQUEST]: function (state) {
    state.status.getPartitionArmingStatus = "requested"
  },
  [GET_PARTITION_ARMING_STATUS_SUCCESS]: function (state, {data, siteId}) {
    const raw = data.partitions || []
    const map = {}
    raw.forEach(entry => {
      const pid = entry.partition_id || entry.id
      if (!pid) return
      const existing = map[pid]
      if (!existing || (entry.arming_state_provider && !existing.arming_state_provider)) {
        map[pid] = entry
      }
    })
    Vue.set(state.armingStatusBySite, siteId, Object.values(map))
    state.status.getPartitionArmingStatus = "successful"
  },
  [GET_PARTITION_ARMING_STATUS_FAILURE]: function (state) {
    state.status.getPartitionArmingStatus = "failed"
  },
  [GET_ALL_SITES_PARTITION_ARMING_STATUS_REQUEST]: function (state) {
    state.status.getAllSitesPartitionArmingStatus = "requested"
  },
  [GET_ALL_SITES_PARTITION_ARMING_STATUS_SUCCESS]: function (state, {data}) {
    const sites = data.sites || {}
    Vue.set(state, 'allSitesPartitionArmingStatus', sites)
    state.status.getAllSitesPartitionArmingStatus = "successful"
  },
  [GET_ALL_SITES_PARTITION_ARMING_STATUS_FAILURE]: function (state) {
    state.status.getAllSitesPartitionArmingStatus = "failed"
  },
  [UPDATE_PARTITION_ARMING_STATUS_REQUEST]: function (state) {
    state.status.updatePartitionArmingStatus = "requested"
  },
  [UPDATE_PARTITION_ARMING_STATUS_SUCCESS]: function (state) {
    state.status.updatePartitionArmingStatus = "successful"
  },
  [UPDATE_PARTITION_ARMING_STATUS_FAILURE]: function (state) {
    state.status.updatePartitionArmingStatus = "failed"
  },
}

export default {
  state,
  getters,
  actions,
  mutations
}
